<template>
    <div class="filters-wrap">
        <!-- Left: filter pills -->
        <div class="filters-scroll">
            <!-- Cruise dates -->
            <v-btn class="filter-pill" variant="tonal" color="primary" rounded="lg" append-icon="mdi-chevron-down"
                @click="datesDlg = true">
                Cruise dates
            </v-btn>

            <!-- Departure Port -->
            <v-btn class="filter-pill" variant="tonal" color="primary" rounded="lg" append-icon="mdi-chevron-down"
                @click="portsDlg = true">
                Departure Port
            </v-btn>

            <!-- Destinations (optional) -->
            <v-btn v-if="showDestinations" class="filter-pill" variant="tonal" color="primary" rounded="lg"
                append-icon="mdi-chevron-down" @click="destDlg = true">
                Destinations
            </v-btn>

            <!-- Number of nights -->
            <v-btn class="filter-pill" variant="tonal" color="primary" rounded="lg" append-icon="mdi-chevron-down"
                @click="nightsDlg = true">
                Number of nights
            </v-btn>

            <!-- Ships -->
            <v-btn v-if="showShips" class="filter-pill" variant="tonal" color="primary" rounded="lg"
                append-icon="mdi-chevron-down" @click="shipsDlg = true">
                Ships
            </v-btn>
        </div>

        <!-- Right: sort -->
        <div class="sort-wrap">
            <span class="mr-3 text-medium-emphasis">Sort by:</span>
            <v-select v-model="local.filters.sort" :items="sortItems" hide-details density="comfortable"
                variant="outlined" class="sort-select" @update:modelValue="emitAll" />
        </div>

        <!-- DIALOGS -->

        <!-- Cruise dates (months grid like screenshot; past months grey/disabled) -->
        <v-dialog v-model="datesDlg" max-width="900">
            <v-card rounded="lg">
                <v-card-title class="d-flex align-center justify-space-between">
                    <div class="text-h6">Cruise dates</div>
                    <v-btn icon variant="text" @click="datesDlg = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider />

                <v-card-text>
                    <div class="years-row">
                        <div v-for="y in years" :key="y" class="year-col">
                            <div class="year-title">{{ y }}</div>

                            <div class="months-grid-4">
                                <v-btn v-for="m in 12" :key="`${y}-${m}`" class="month-btn"
                                    :variant="isSelected(y, m) ? 'elevated' : (isPastMonth(y, m) ? 'flat' : 'outlined')"
                                    :color="isSelected(y, m) ? 'primary' : undefined" :disabled="isPastMonth(y, m)"
                                    :class="{
                                        'is-disabled': isPastMonth(y, m),
                                        'is-selected': isSelected(y, m),
                                    }" @click="toggleMonth(y, m)">
                                    {{ monthName(m) }}
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </v-card-text>

                <v-divider />
                <v-card-actions class="justify-space-between">
                    <v-btn variant="text" @click="resetDates">Reset all</v-btn>
                    <div>
                        <v-btn variant="text" class="mr-1" @click="datesDlg = false">Cancel</v-btn>
                        <v-btn color="primary" @click="applyDates">Apply</v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Departure Port (grouped) -->
        <v-dialog v-model="portsDlg" max-width="1024">
            <v-card rounded="lg" class="ports-card dlg-card">
                <v-card-title class="d-flex align-center">
                    <div class="text-h6 mx-auto">Departure Port</div>
                    <v-spacer />
                    <v-btn icon variant="text" @click="portsDlg = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider />

                <v-card-text class="pb-2 dlg-body">
                    <div v-for="(list, region, idx) in groupedPorts" :key="region" class="region-section">
                        <div class="region-title">{{ region }}</div>

                        <div class="chip-wrap">
                            <v-btn v-for="p in list" :key="p.value" class="chip-pill"
                                :variant="isPortSelected(p.value) ? 'elevated' : 'outlined'"
                                :color="isPortSelected(p.value) ? 'primary' : undefined" rounded="xl"
                                @click="togglePort(p.value)">
                                {{ p.title }}
                            </v-btn>
                        </div>

                        <v-divider class="my-6" v-if="idx < Object.keys(groupedPorts).length - 1" />
                    </div>
                </v-card-text>

                <v-divider />

                <v-card-actions class="justify-end dlg-actions">
                    <v-btn variant="outlined" @click="resetPorts">Reset all</v-btn>
                    <v-btn color="primary" @click="applyPorts">Apply</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Destinations (optional) -->
        <v-dialog v-model="destDlg" max-width="560">
            <v-card rounded="lg">
                <v-card-title class="text-h6">Destinations</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-autocomplete v-model="local.filters.destinations" :items="destinationItems"
                        label="Select destinations" variant="outlined" density="comfortable" multiple chips
                        closable-chips clearable />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="text" @click="destDlg = false">Close</v-btn>
                    <v-btn color="primary" @click="applyDestinations">Apply</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Nights -->
        <v-dialog v-model="nightsDlg" max-width="560">
            <v-card rounded="lg">
                <v-card-title class="d-flex align-center justify-space-between">
                    <div class="text-h6">Number of nights</div>
                    <v-btn icon variant="text" @click="nightsDlg = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />

                <v-card-text>
                    <div class="nights-chip-grid">
                        <v-btn v-for="p in nightPresets" :key="p.key" class="night-chip"
                            :variant="selectedNightsKey === p.key ? 'elevated' : 'outlined'"
                            :color="selectedNightsKey === p.key ? 'primary' : undefined" rounded="lg"
                            @click="pickNightsPreset(p)">
                            {{ p.label }}
                        </v-btn>
                    </div>
                </v-card-text>

                <v-divider />
                <v-card-actions class="justify-space-between">
                    <v-btn variant="text" @click="resetNights">Reset all</v-btn>
                    <div>
                        <v-btn variant="text" class="mr-1" @click="nightsDlg = false">Cancel</v-btn>
                        <v-btn color="primary" @click="applyNights">Apply</v-btn>
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Ships -->
        <v-dialog v-model="shipsDlg" max-width="560">
            <v-card rounded="lg">
                <v-card-title class="text-h6">Ships</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-autocomplete v-model="local.filters.ships" :items="shipItems" label="Select ships"
                        variant="outlined" density="comfortable" multiple chips closable-chips clearable />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="text" @click="shipsDlg = false">Close</v-btn>
                    <v-btn color="primary" @click="applyShips">Apply</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Guests (optional placeholder) -->
        <v-dialog v-model="guestsDlg" max-width="420">
            <v-card rounded="lg">
                <v-card-title class="text-h6">Number of guests</v-card-title>
                <v-divider />
                <v-card-text>
                    <v-slider v-model="local.filters.guests" :min="1" :max="8" step="1" thumb-label="always" />
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn variant="text" @click="guestsDlg = false">Close</v-btn>
                    <v-btn color="primary" @click="applyGuests">Apply</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { computed, reactive, ref, watch } from 'vue'

/* ---------------- Props / v-model ---------------- */
const props = defineProps({
    modelValue: { type: Object, required: true },
    portItems: { type: Array, default: () => [] },
    shipItems: { type: Array, default: () => [] },
    destinationItems: { type: Array, default: () => [] },
    nightsMin: { type: Number, default: 1 },
    nightsMax: { type: Number, default: 20 },
    sortItems: {
        type: Array,
        default: () => ([
            { title: 'Recommended', value: 'rec' },
            { title: 'Date — Soonest', value: 'date-asc' },
            { title: 'Nights — Shortest', value: 'nights-asc' },
            { title: 'Title — A → Z', value: 'title-asc' },
        ])
    },
    showDestinations: { type: Boolean, default: false },
    showGuests: { type: Boolean, default: false },
    showShips: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

/* Local copy so Apply/Cancel makes sense */
const local = reactive({
    filters: JSON.parse(JSON.stringify(props.modelValue || {}))
})

/* Sync with parent */
const emitAll = () => emit('update:modelValue', JSON.parse(JSON.stringify(local.filters)))
watch(() => props.modelValue, (v) => {
    local.filters = JSON.parse(JSON.stringify(v || {}))
}, { deep: true })

/* ---------------- Dialog state ---------------- */
const datesDlg = ref(false)
const portsDlg = ref(false)
const nightsDlg = ref(false)
const shipsDlg = ref(false)
const destDlg = ref(false)
const guestsDlg = ref(false)

/* ---------------- Months grid helpers ---------------- */
const now = new Date()
const startYear = now.getFullYear()
const years = [startYear, startYear + 1, startYear + 2]
const keyYM = (y, m) => `${y}-${String(m).padStart(2, '0')}`

const monthsSet = computed(() => new Set(local.filters.months || []))
const monthName = (m) => new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'short' })
const isSelected = (y, m) => monthsSet.value.has(keyYM(y, m))
const toggleMonth = (y, m) => {
    const key = keyYM(y, m)
    const set = new Set(local.filters.months || [])
    set.has(key) ? set.delete(key) : set.add(key)
    local.filters.months = [...set]
}

// Past-month logic (grey + disabled)
const TODAY = new Date()
const THIS_YEAR = TODAY.getFullYear()
const THIS_MONTH = TODAY.getMonth() + 1 // 1-12
const isPastMonth = (y, m) => (y < THIS_YEAR) || (y === THIS_YEAR && m < THIS_MONTH)

/* ---------------- Departure port grouping ---------------- */
// Light heuristics for region guessing from label tail
const NA = ['united states', 'usa', 'u.s.', 'america', 'canada', 'louisiana', 'maryland', 'massachusetts', 'mexico', 'puerto rico', 'dominican republic', 'bahamas', 'bermuda', 'cuba', 'jamaica', 'panama', 'colombia', 'aruba', 'curacao', 'cayman', 'british columbia', 'alaska', 'hawaii', 'florida', 'california', 'texas', 'new jersey', 'washington']
const EU = ['spain', 'italy', 'greece', 'england', 'united kingdom', 'uk', 'france', 'portugal', 'netherlands', 'germany', 'norway', 'sweden', 'denmark', 'ireland', 'iceland']
const AU = ['australia', 'new zealand', 'sydney', 'brisbane', 'melbourne', 'auckland', 'wellington']
const LA = ['colombia', 'panama', 'costa rica', 'puerto rico', 'dominican republic', 'argentina', 'chile', 'brazil', 'mexico', 'belize', 'honduras', 'guatemala', 'caribbean', 'st. ', 'saint ']
const AS = ['china', 'singapore', 'korea', 'india', 'japan', 'indonesia']

function guessRegion(label) {
    const s = String(label).toLowerCase()
    if (AU.some(w => s.includes(w))) return 'Australia'
    if (EU.some(w => s.includes(w))) return 'Europe'
    if (LA.some(w => s.includes(w))) return 'Latin America & Caribbean'
    if (NA.some(w => s.includes(w))) return 'North America'
    if (AS.some(w => s.includes(w))) return 'Asia & The Pacific'
    return 'Other'
}

// normalize item -> { title, value, region }
function normalizePort(it) {
    if (typeof it === 'string') return { title: it, value: it, region: guessRegion(it) }
    const title = it && (it.title ?? it.value ?? '')
    const value = it && (it.value ?? it.title ?? '')
    const region = (it && it.region) ? it.region : guessRegion(title || value)
    return { title, value, region }
}

const groupedPorts = computed(() => {
    const items = (props.portItems || []).map(normalizePort)
    const groups = {}
    for (const p of items) {
        const r = p.region || 'Other'
        if (!groups[r]) groups[r] = []
        groups[r].push({ title: p.title, value: p.value })
    }
    const order = ['North America', 'Europe', 'Australia', 'Latin America & Caribbean', 'Asia & The Pacific', 'Other']
    const out = {}
    for (const r of order) {
        if (groups[r]?.length) out[r] = groups[r].sort((a, b) => a.title.localeCompare(b.title))
    }
    for (const r of Object.keys(groups).sort()) {
        if (!(r in out)) out[r] = groups[r].sort((a, b) => a.title.localeCompare(b.title))
    }
    return out
})

const selectedPorts = computed(() => new Set(local.filters.ports || []))
const isPortSelected = (val) => selectedPorts.value.has(val)
function togglePort(val) {
    const set = new Set(local.filters.ports || [])
    set.has(val) ? set.delete(val) : set.add(val)
    local.filters.ports = [...set]
}
function resetPorts() {
    local.filters.ports = []
}

/* Nights */
// Preset chips (keeps filters.nights as [min,max] so your page logic still works)
const nightPresets = [
  { key: '2-5',  label: '2–5 nights',  range: [2, 5] },
  { key: '6-8',  label: '6–8 nights',  range: [6, 8] },
  { key: '9-11', label: '9–11 nights', range: [9, 11] },
  { key: '12+',  label: '12+ nights',  range: [12, 99] }, // 99 ~ upper bound
]

const selectedNightsKey = ref(null)

function syncNightsKeyFromRange() {
  const [a, b] = local.filters.nights || []
  const found = nightPresets.find(p => p.range[0] === a && p.range[1] === b)
  selectedNightsKey.value = found ? found.key : null
}

// keep selection in sync when dialog opens or when value changes externally
watch(() => nightsDlg.value, open => { if (open) syncNightsKeyFromRange() })
watch(() => local.filters.nights, syncNightsKeyFromRange, { deep: true })

function pickNightsPreset(preset) {
  selectedNightsKey.value = preset.key
  local.filters.nights = [...preset.range]
}

function resetNights() {
  selectedNightsKey.value = null
  local.filters.nights = [props.nightsMin, props.nightsMax]
}

/* ---------------- Apply / Reset handlers ---------------- */
function resetDates() {
    local.filters.months = []
    local.filters.dateRange = null
}
function applyDates() { datesDlg.value = false; emitAll() }
function applyPorts() { portsDlg.value = false; emitAll() }
function applyNights() { nightsDlg.value = false; emitAll() }
function applyShips() { shipsDlg.value = false; emitAll() }
function applyDestinations() { destDlg.value = false; emitAll() }
function applyGuests() { guestsDlg.value = false; emitAll() }
</script>

<style scoped>
.filters-wrap {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
}

.filters-scroll {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 4px;
}

.filter-pill {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
    border-radius: 10px;
}

.sort-wrap {
    display: flex;
    align-items: center;
}

.sort-select {
    min-width: 210px;
}

.dlg-card {
  display: flex;
  flex-direction: column;
  /* keep the dialog within the viewport */
  max-height: 80vh;
}

.dlg-body {
  /* make the content area take remaining space and scroll */
  flex: 1 1 auto;
  overflow-y: auto;
}

.dlg-actions {
  /* optional: subtle divider look is already added above with <v-divider /> */
  padding-top: 8px;
}

/* Months layout */
.years-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 28px;
}

.year-col {
    min-width: 0;
}

.year-title {
    font-weight: 700;
    color: #1a237e;
    margin-bottom: 10px;
}

.months-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
}

.month-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 700;
    border-radius: 999px;
}

.month-btn.is-disabled {
    opacity: 0.45;
    pointer-events: none;
}

/* Ports dialog */
.ports-card {
    overflow: hidden;
}

.region-section {
    padding: 5px 0;
}

.region-title {
    font-weight: 700;
    font-size: 1.05rem;
    color: #1a237e;
    margin: 12px 0 10px;
}

.chip-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.chip-pill {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
    padding: 10px 16px;
    border-width: 2px;
}

@media (max-width: 960px) {
    .filters-wrap {
        grid-template-columns: 1fr;
    }

    .sort-wrap {
        justify-content: flex-start;
    }

    .years-row {
        grid-template-columns: 1fr;
        gap: 16px;
    }
}

.nights-chip-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.night-chip {
  text-transform: none;
  font-weight: 600;
}
@media (min-width: 640px) {
  .nights-chip-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

</style>